import LoadingScreen from "components/LoadingScreen";
import { FC, Props } from "./typings";
import { useContext, useEffect, useState } from "react";
import { getKeys } from "utils/getKeys";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { getUserIsActivated } from "utils/getUserIsActivated";
import InvitationsToWorkspace from "components/InvitationsToWorkspace";
import workspaceService from "api/workspaceService";
import CompanyContext from "contexts/CompanyContext";
import companyService from "api/companyService";
import NoCompanyGate from "components/NoCompanyGate";
import { shouldShowNoCompanyGate } from "components/NoCompanyGate/shouldShow";
import CompanyKeyBootstrapGate from "components/CompanyKeyBootstrapGate";
import { shouldShowCompanyKeyBootstrapGate } from "components/CompanyKeyBootstrapGate/shouldShow";
import companyKeysService from "api/companyKeysService";
import CreateWorkspace from "components/CreateWorkspace";
import WorkspaceContext from "contexts/CurrentWorkspace";
import { setWorkspaceId } from "utils/setWorkspaceId";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import keysService from "api/keysService";
import settingsService from "api/settingsService";
import ModalsManagerContext from "contexts/ModalsManager";
import { logger } from "utils/logger";
import { getUserIdentityVerified } from "utils/getUserIdentityVerified";
import { useMediaQuery } from "usehooks-ts";

const ProtectedRoute: FC<Props> = ({ SignalRContext, children }) => {
  // Bieżący zespół to dziś po prostu FIRMA (Workspaces — ten sam wiersz bazy, patrz
  // docs/model-kluczy-firmowych.md, sekcja 1): CompanyContext niesie tożsamość, a pełną
  // strukturę/członków/role dociąga się osobno po jej Id (api/workspace/current zostało
  // usunięte, ale api/workspace?id= — GET pojedynczej przestrzeni — nadal działa).
  const { company, hasCompany, isLoading } = useContext(CompanyContext);

  // To samo zapytanie co w CompanyProvider (ten sam klucz React Query — jeden request),
  // ale potrzebny jest tu SAM BLĄD: kontekst niesie wyłącznie `hasCompany`, a awaria sieci
  // wygląda w nim identycznie jak konto bez firmy. Bramka nie może się na tym pomylić.
  const { isError: isCompanyError } = companyService.useGetCurrentCompany(true);

  // Generacje klucza firmy — pytamy DOPIERO gdy firma jest znana, żeby konto bez firmy
  // (D2) nie generowało ruchu na endpoint, który i tak odpowie „brak firmy".
  const {
    data: companyKeys,
    isLoading: isCompanyKeysLoading,
    isError: isCompanyKeysError,
  } = companyKeysService.useGetCompanyKeys(hasCompany);

  const { data: workspaceDetails } = workspaceService.useGetWorkspace(
    company?.Id ?? "",
  );

  const currentWorkspace = company ? workspaceDetails?.data : undefined;

  const { modals, setModals } = useContext(ModalsManagerContext);

  const { data: workspacesData, isLoading: loadingWorkspaces } =
    workspaceService.useGetWorkspaces();

  const {
    isSuccess: isSuccessRecoveryPackage,
    isError: isErrorRecoveryPackage,
  } = keysService.useGetRecoveryPackage();

  const { mutate: switchWorkspace } = workspaceService.useWorkspaceSwitch();

  const { workspace, setWorkspace } = useContext(WorkspaceContext);

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [invittedToWorkspaces, setInvitedToWorkspaces] = useState([]);
  const [showList, setShowList] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);

  SignalRContext.useSignalREffect(
    "invitations",
    (data) => {
      setInvitedToWorkspaces(data);
      setShowList(true);
    },
    [],
  );

  const initUser = async () => {
    if (auth.user) {
      const userId = getUserId(auth.user);
      const isActivated = getUserIsActivated(auth.user);

      const keys = await getKeys(userId!);

      // Brak lokalnych kluczy — jedyne wyjście to odzyskanie dostępu. Furtka
      // `if (VITE_ENV === "dev") {}`, która dawniej pomijała to przekierowanie na dev,
      // została USUNIĘTA świadomie: w modelu kluczy firmowych sesja bez kluczy nie odszyfruje
      // niczego (klucz firmy odtwarza się z grantu kluczem POŚREDNIM), więc „działająca"
      // sesja deweloperska bez kluczy pokazywałaby wyłącznie błędy deszyfracji — i różniła się
      // od produkcji dokładnie w tym miejscu, które najbardziej wymaga testowania.
      if (
        isSuccessRecoveryPackage &&
        !keys &&
        window.location.pathname.search("recovery") === -1
      ) {
        //if (import.meta.env.VITE_ENV === "dev") {
        //} else {
        navigate("/recovery", { replace: true });
        //}
      }

      const userClaim = getUserClaim(auth.user!, Claims.invitation);

      if (!!userClaim?.length && isErrorRecoveryPackage) {
        navigate("/activation", { replace: true });
        return;
      }

      if (!isActivated && !userClaim?.length) {
        navigate("/activation", {
          replace: true,
        });
      }
    }
  };

  // Stan początkowy (m.in. ShowFirstConfiguration) pobieramy dopiero gdy konto jest
  // w pełni aktywowane. W trakcie aktywacji (activationModule renderowany wewnątrz
  // ProtectedRoute) klucze/tożsamość nie są jeszcze gotowe — gdyby zapytanie poszło
  // wtedy, wynik zostałby zcache'owany zanim warunki pokazania kreatora są spełnione,
  // przez co kreator pierwszej aktywacji pojawiał się dopiero przy drugim wejściu.
  const isActivated = auth?.user ? getUserIsActivated(auth.user) : false;
  const { data: initialConfig } = settingsService.useGetInitialState(
    !auth?.user || !isActivated,
  );

  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    (async function () {
      if (auth.user) {
        const userId = getUserId(auth.user);
        const keys = await getKeys(userId!);
        if (
          initialConfig?.data.ShowFirstConfiguration &&
          getUserIdentityVerified(auth.user) &&
          keys
        ) {
          if (!isMobile) {
            logger.log("Show first configuration");
            setModals({ ...modals, initialConfig: true });
          }
        }
      }
    })();
  }, [initialConfig?.data]);

  useEffect(() => {
    initUser();
  }, [auth.user, isErrorRecoveryPackage, isSuccessRecoveryPackage]);

  useEffect(() => {
    if (isLoading || loadingWorkspaces) {
      return;
    }

    // Bierzemy zespół wprost z firmy zalogowanego (D1 — jedna firma, bez przełącznika).
    if (currentWorkspace && !workspace) {
      setWorkspace(currentWorkspace);
      setWorkspaceId(currentWorkspace.Id ?? "");

      return;
    }

    // Konto bez firmy (D2) albo szczegóły zespołu jeszcze niedociągnięte — awaryjnie
    // ustaw pierwszy zespół z listy (zachowanie jak dla dawnego braku bieżącego zespołu).
    if (!currentWorkspace) {
      const workspaces = workspacesData?.data?.Items;
      const countWorkspaces = workspaces?.length;

      localStorage.removeItem("workspaceId");
      sessionStorage.removeItem("workspaceId");

      if (!workspaces) return;

      if (countWorkspaces === 1) {
        setWorkspace(workspaces[0]);
        setWorkspaceId(workspaces[0].Id);
        // Ustawiamy pierwszy z brzegu

        switchWorkspace({
          Id: workspaces[0].Id,
        });

        return;
      }

      if (countWorkspaces === 0) {
        return;
      }

      // navigate('/choose-workspace');
      return;
    }
  }, [isLoading, currentWorkspace, loadingWorkspaces]);

  if (isLoading || loadingWorkspaces) {
    return <LoadingScreen />;
  }

  // Konto bez firmy: bez niej getCompanyId() jest nullem, listy zawężają się do pustego
  // WorkspaceId, a każdy zapis kończy się CompanyKeyMissingError — wszystko bez słowa
  // wyjaśnienia. Zamiast tego pokazujemy jedyne dwa wyjścia: załóż albo przyjmij zaproszenie.
  if (
    shouldShowNoCompanyGate({
      hasCompany,
      isLoading,
      isError: isCompanyError,
      pathname: location.pathname,
    })
  ) {
    if (creatingCompany) {
      return (
        <div className="flex min-h-screen items-start justify-center p-6">
          <div className="w-full max-w-[560px]">
            <CreateWorkspace
              onCreate={() => {
                // Firma powstaje po stronie BE; najprostszym i najpewniejszym sposobem
                // przeładowania CAŁEGO stanu sesji (klucz firmy, uprawnienia, listy) jest
                // wejście w aplikację od nowa.
                window.location.replace("/");
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <>
        <NoCompanyGate
          onCreateCompany={() => setCreatingCompany(true)}
          onAcceptInvitation={
            invittedToWorkspaces.length ? () => setShowList(true) : undefined
          }
          pendingInvitationsCount={invittedToWorkspaces.length}
        />
        {showList && (
          <InvitationsToWorkspace
            toggle={() => setShowList(false)}
            workspaces={invittedToWorkspaces}
          />
        )}
      </>
    );
  }

  // Firma JEST, ale nie ma jeszcze klucza szyfrującego (firmy sprzed modelu kluczy
  // firmowych). NoCompanyGate tego nie łapie — dla niej `hasCompany` jest prawdą. Bez tej
  // bramki użytkownik dostaje pełny interfejs, w którym każdy zapis odbija się
  // o CompanyKeyMissingError, a treść nie odszyfrowuje się wcale.
  if (
    shouldShowCompanyKeyBootstrapGate({
      hasCompany,
      isLoading: isLoading || isCompanyKeysLoading,
      isError: isCompanyKeysError,
      activeKeyId: companyKeys?.data?.ActiveKeyId,
      pathname: location.pathname,
    })
  ) {
    return <CompanyKeyBootstrapGate />;
  }

  return (
    <>
      {children}
      {showList && (
        <InvitationsToWorkspace
          toggle={() => setShowList(false)}
          workspaces={invittedToWorkspaces}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
